import {
  OptionType,
  RelationInstanceSelect,
} from "./connect-or-disconect-relations/relation-instance-select";
import { PyloUserRelationSelect } from "./connect-or-disconect-relations/pylo-user-relation-select";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import {
  EntityInstance,
  ItemsToConnect,
  ItemsToDisconnect,
  ItemsToSet,
} from "@/services/entity-instance.service";
import { useEntitySchemaByName } from "@/hooks/schema/use-entity-schema-by-name";
import { useEntityInstanceList } from "@/hooks/use-entity-instance-list";
import { useRelationEntityList } from "@/hooks/use-relation-entity-list";
import { useEntityInstanceLayoutStore } from "@/stores/entity-instance-field-store";
import { useDebounce } from "@/lib/client-utils";
import { useEntityByName } from "@/hooks/schema/use-entity-by-name";
import { usePermissionForEntityId } from "@/hooks/schema/use-my-permissions";
import { EntityViewComponentMapping } from "@/services/entity-view-components.service";
import { useEntityStore } from "@/stores/entity.store";

export const getSelectedIds = (
  value: {
    data: Array<object> | object | undefined;
  },
  pages: Array<{ data: object | Array<object> }>,
) => {
  if (Array.isArray(value.data)) {
    const elements = [...value.data, ...pages?.filter((x) => !!x).flatMap((p) => p.data)];
    return new Set(elements.map((x) => (x as { id: string }).id));
  }
  return new Set([]);
};

export const getSelectedId = (value: { data: Array<object> | object | undefined }) => {
  if (typeof value.data === "object") {
    return (value.data as { id: string }).id;
  }
  return "";
};

export const ConnectOrDisconnectEntityRelation = ({
  fieldName,
  entityType,
  setItemsToConnect,
  setItemsToDisconnect,
  setItemsToSet,
  itemsToSet,
  itemsToDisconnect,
  itemsToConnect,
  parentEntityId,
  parentEntityName,
  isList,
  cachedValue,
  relationFieldToDisplay,
  fieldsToFetch,
  mapping,
  isTightlyCoupled,
}: {
  // field:
  parentEntityName: string;
  parentEntityId?: string;
  fieldName: string;
  entityType: string;
  itemsToConnect: ItemsToConnect;
  itemsToDisconnect: ItemsToDisconnect;
  itemsToSet: ItemsToSet;
  setItemsToConnect: Dispatch<SetStateAction<ItemsToConnect>>;
  setItemsToDisconnect: Dispatch<SetStateAction<ItemsToDisconnect>>;
  setItemsToSet: Dispatch<SetStateAction<ItemsToSet>>;
  isList: boolean;
  cachedValue?: OptionType;
  relationFieldToDisplay?: string;
  fieldsToFetch?: Array<string>;
  mapping?: EntityViewComponentMapping;
  isTightlyCoupled?: boolean;
}) => {
  // Derive selection purely from server data and user deltas (no syncing effects)

  const entityNameForRelationOptions = useMemo(() => {
    // the name depends on the relation type
    if (entityType.endsWith("ListData")) {
      return entityType.replace("ListData", "");
    } else if (entityType.endsWith("Data")) {
      return entityType.replace("Data", "");
    } else return entityType;
  }, [entityType]);

  const { entitySchema } = useEntitySchemaByName(entityNameForRelationOptions);
  const { entity } = useEntityByName(entityNameForRelationOptions);
  const { getEntityByName } = useEntityStore();

  const entityFromStore = getEntityByName(entityNameForRelationOptions);
  const permissions = usePermissionForEntityId(entityFromStore?.id ?? entity?.id ?? "");
  const hasReadPermission = permissions.has_read;

  const {
    state: { field: defaultField },
  } = useEntityInstanceLayoutStore(entityNameForRelationOptions);

  const displayField = useMemo(() => {
    // Check if relationFieldToDisplay exists in the entity's fields
    if (relationFieldToDisplay) {
      const entityFields = entityFromStore?.entity_fields?.data ?? [];
      const fieldExists = entityFields.some((f) => f.name === relationFieldToDisplay);
      if (fieldExists) {
        return relationFieldToDisplay;
      }
    }
    // Fall back to default field if relationFieldToDisplay is not set or doesn't exist
    return defaultField;
  }, [relationFieldToDisplay, defaultField, entityFromStore]);

  const relationFieldsToFetch = useMemo(() => {
    const fields = [];
    fields.push("id");

    // if we dont have fields to fetch (when we don't have a compnent mapping defined for this entity) we use the display field
    if (!fieldsToFetch) {
      fields.push(displayField);
      return fields;
    }

    for (const field of fieldsToFetch) {
      fields.push(field);
    }
    return fields;
  }, [fieldsToFetch, displayField]);

  const infiniteSelectedInstancesForRelation = useRelationEntityList({
    fieldName: fieldName,
    id: parentEntityId,
    entityName: parentEntityName,
    fieldsToFetchForRelationFieldNames: [
      {
        fieldName: fieldName,
        fieldsToFetch: relationFieldsToFetch,
      },
    ],
    enabled: hasReadPermission,
  });

  const [optionSearch, setOptionSearch] = useState("");

  const debouncedSearch = useDebounce(optionSearch, 300);

  const infiniteOptions = useEntityInstanceList({
    entityName: entityNameForRelationOptions,
    ...(debouncedSearch
      ? {
          filter: {
            // sortby: [{ field: 'created_at', order: 'DESC' }],
            query: [
              {
                condition: {
                  field: displayField,
                  operator: "ilike",
                  value: `%${debouncedSearch}%`,
                },
              },
            ],
          },
        }
      : {}),
    fetchAllFields: false,
    fieldsToFetch: Array.from(
      new Set(["id", "integer_id", displayField, ...(fieldsToFetch ?? [])]),
    ),
    enabled: hasReadPermission,
  });

  // Compute server-selected ids once per data change
  const serverSelectedIds = useMemo(() => {
    const selectedData =
      infiniteSelectedInstancesForRelation.data?.pages
        .flatMap((x) => x?.data)
        .filter((x) => x !== null) ?? [];
    return new Set(selectedData.map((x) => (x as { id: string })?.id).filter(Boolean));
  }, [infiniteSelectedInstancesForRelation.data?.pages]);

  const disconnectIds = useMemo(
    () => new Set(itemsToDisconnect?.[fieldName]?.map((x) => x.id) ?? []),
    [itemsToDisconnect, fieldName],
  );

  const connectIds = useMemo(
    () => new Set(itemsToConnect?.[fieldName]?.map((x) => x.id) ?? []),
    [itemsToConnect, fieldName],
  );

  const setIds = useMemo(() => {
    const ids = new Array<string>();
    if (Array.isArray(itemsToSet?.[fieldName])) {
      ids.push(...(itemsToSet?.[fieldName]?.map((x) => x.id) ?? []));
    } else if (itemsToSet?.[fieldName]?.id) {
      ids.push(itemsToSet?.[fieldName]?.id);
    }
    return new Set(ids);
  }, [itemsToSet, fieldName]);

  // Check if user has explicitly set a value for this field (even if empty array)
  const hasUserSetValue = fieldName in (itemsToSet ?? {});

  const effectiveSelectedIds = useMemo(() => {
    if (!isList) {
      // If user has explicitly set a value, use it (even if empty)
      // Otherwise fall back to server data
      return hasUserSetValue ? setIds : serverSelectedIds;
    }
    const ids = new Set<string>([...serverSelectedIds, ...connectIds]);
    for (const id of disconnectIds) ids.delete(id);
    return ids;
  }, [isList, serverSelectedIds, connectIds, disconnectIds, setIds, hasUserSetValue]);

  const effectiveSelectedInstances: EntityInstance[] = useMemo(
    () => Array.from(effectiveSelectedIds).map((id) => ({ id })),
    [effectiveSelectedIds],
  );

  const effectiveSelectedSingle: EntityInstance | null = useMemo(() => {
    const setValueRaw = itemsToSet?.[fieldName];

    // If set to a single entity instance
    const setValue = setValueRaw as EntityInstance | undefined;
    if (setValue?.id) return { id: setValue.id };

    // Fall back to server-selected value
    const first = Array.from(effectiveSelectedIds)[0];
    return first ? { id: first } : null;
  }, [itemsToSet, fieldName, effectiveSelectedIds]);

  const handleSelect = (instance: EntityInstance) => {
    if (isList) {
      // Always: add to connect (dedupe) and remove from disconnect if present
      setItemsToConnect((prev) => ({
        ...prev,
        [fieldName]: [
          ...new Map(
            ([...(prev?.[fieldName] ?? []), instance] as EntityInstance[]).map((it) => [it.id, it]),
          ).values(),
        ],
      }));
      setItemsToDisconnect((prev) => ({
        ...prev,
        [fieldName]: prev?.[fieldName]?.filter((x) => x.id !== instance.id) ?? [],
      }));
    } else {
      setItemsToSet((prev) => ({ ...prev, [fieldName]: instance }));
    }
  };

  const handleUnselect = (id: string) => {
    if (isList) {
      // Always: add to disconnect (dedupe) and remove from connect if present
      setItemsToDisconnect((prev) => ({
        ...prev,
        [fieldName]: [
          ...new Map(
            ([...(prev?.[fieldName] ?? []), { id }] as EntityInstance[]).map((it) => [it.id, it]),
          ).values(),
        ],
      }));
      setItemsToConnect((prev) => ({
        ...prev,
        [fieldName]: prev?.[fieldName]?.filter((x) => x.id !== id) ?? [],
      }));
    } else {
      setItemsToSet?.((prev) => ({ ...prev, [fieldName]: null }));
    }
  };

  // Show disabled state when user doesn't have read permission on the related entity
  if (!hasReadPermission && (entityFromStore?.id || entity?.id)) {
    return (
      <div className="flex h-10 items-center rounded-md border border-dashed px-3 opacity-50 cursor-not-allowed">
        <span className="text-sm text-muted-foreground">
          No permission to view {entityNameForRelationOptions}
        </span>
      </div>
    );
  }

  // Use specialized PyloUser selector when target entity is PyloUser
  if (entityNameForRelationOptions === "PyloUser") {
    return (
      <PyloUserRelationSelect
        isSingle={!isList}
        selectedInstance={effectiveSelectedSingle}
        selectedInstances={effectiveSelectedInstances}
        onSelect={handleSelect}
        onUnselect={handleUnselect}
        idsToDisconnect={new Set(itemsToDisconnect?.[fieldName]?.map((x) => x.id) ?? [])}
        parentEntityId={parentEntityId}
        parentEntityName={parentEntityName}
        fieldName={fieldName}
      />
    );
  }

  return (
    <>
      <RelationInstanceSelect
        entityName={entityNameForRelationOptions}
        selectedOptions={infiniteSelectedInstancesForRelation}
        options={infiniteOptions}
        isSingle={!isList}
        entitySchema={entitySchema!}
        selectedInstance={effectiveSelectedSingle}
        selectedInstances={effectiveSelectedInstances}
        onSelect={handleSelect}
        onUnselect={handleUnselect}
        idsToDisconnect={new Set(itemsToDisconnect?.[fieldName]?.map((x) => x.id) ?? [])}
        value={cachedValue}
        onOptionSearchChange={setOptionSearch}
        optionSearchStr={optionSearch}
        permissions={permissions}
        relationFieldToDisplay={relationFieldToDisplay}
        mapping={mapping}
        isTightlyCoupled={isTightlyCoupled}
      />
    </>
  );
};
